import type { WorkingViewDefinition } from '@/stores/workingViewStore';
import { isPropStat, type PropStat } from '@/lib/sport/propStat';
import type { Period, PriceMode } from '../ValueDashboard/types';

export interface GameLinesReading {
  priceMode: PriceMode;
  marketSide: 'yes' | 'no';
  betType: 'spread' | 'total' | 'moneyline' | 'btts' | 'race' | 'overtime';
  hideNoVol: boolean;
  hideSettled: boolean;
  period: Period;
  tab: 'lines' | 'props' | 'events';
  propStat: PropStat;
}
/** The working-view instance of a Game Detail section: the game or event key
 * plus the URL focus (a focus link reads its own record). Shared with the
 * Recent views menu, which reads the record a Recent return would resume. */
export const readingInstance = (key: string, focusTicker?: string | null, focusSide?: string | null) =>
  JSON.stringify([key, focusTicker ?? null, focusSide ?? null]);

/** #1238 — what a Game Detail URL focuses for its reading records and scroll.
 * A `?ticker` link and a `?venue=polymarket_us` link are both focus visits:
 * each reads its own section records (never the plain one Recent views
 * resumes) and does not restore the page's scroll. Built once by the page and
 * handed to every section, so the call sites cannot drift. */
export function gameDetailFocus(params: URLSearchParams): { readingFocus: string | null; restoreScroll: boolean } {
  const ticker = params.get('ticker');
  if (ticker) return { readingFocus: ticker, restoreScroll: false };
  if (params.get('venue') === 'polymarket_us') {
    return { readingFocus: `us:${params.get('usMarket') || 'moneyline'}`, restoreScroll: false };
  }
  return { readingFocus: null, restoreScroll: true };
}

// Game Lines' own labels, here so the Recent views menu names a view in the
// section's words without a second copy.
export const SECTION_TABS: ReadonlyArray<{ id: GameLinesReading['tab']; label: string }> = [
  { id: 'lines', label: 'Game Lines' },
  { id: 'props', label: 'Player Props' },
  // Kalshi's own name for the family (Dan, 2026-08-30); the tab id stays 'events'.
  { id: 'events', label: 'Game Props' },
];

export const BET_TYPES: ReadonlyArray<{ id: GameLinesReading['betType']; label: string }> = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'total', label: 'Point Total' },
  { id: 'spread', label: 'Spread' },
  // Soccer + football quarters — both teams to score. A single binary per game/period.
  { id: 'btts', label: 'BTTS' },
  // NFL only (nfl-game-lines-props), where Kalshi files them under Game
  // Lines beside Spread / Point Total / Moneyline (Dan, 2026-09-02 screenshot):
  // the 3-way "Race to Points" per points line, and the overtime yes/no.
  { id: 'race', label: 'Race to Points' },
  { id: 'overtime', label: 'Overtime' },
];
// Selectable periods (data-driven enable below). Tab order matches Kalshi's
// (1st Half / 2nd Half / … / Full Game). 'full' only ever appears for BTTS,
// which has no top section; spread/total/moneyline full game live up top.
export const PERIODS: ReadonlyArray<{ id: Period; label: string }> = [
  { id: '1H', label: '1st Half' },
  { id: '2H', label: '2nd Half' },
  // MLB First 5 Innings (Kalshi-only). MLB lists no halves/quarters, so this is
  // the only period MLB games ever show here.
  { id: 'F5', label: 'First 5 Innings' },
  { id: '1Q', label: '1st Quarter' },
  { id: '2Q', label: '2nd Quarter' },
  { id: '3Q', label: '3rd Quarter' },
  { id: '4Q', label: '4th Quarter' },
  // NHL regulation periods (#1218, Kalshi-only).
  { id: '1P', label: '1st Period' },
  { id: '2P', label: '2nd Period' },
  { id: '3P', label: '3rd Period' },
  { id: 'full', label: 'Full Game' },
];

export const gameLinesReading: WorkingViewDefinition<GameLinesReading> = {
  id: 'game-lines-reading-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<GameLinesReading>;
    if (!['maker', 'taker', 'market'].includes(v.priceMode ?? '')
      || !['yes', 'no'].includes(v.marketSide ?? '')
      || !['spread', 'total', 'moneyline', 'btts', 'race', 'overtime'].includes(v.betType ?? '')
      || typeof v.hideNoVol !== 'boolean' || typeof v.hideSettled !== 'boolean'
      || !['full', '1H', '2H', 'F5', '1Q', '2Q', '3Q', '4Q', '1P', '2P', '3P'].includes(v.period ?? '')
      || !['lines', 'props', 'events'].includes(v.tab ?? '')
      || !v.propStat || !isPropStat(v.propStat)) return null;
    return { priceMode: v.priceMode!, marketSide: v.marketSide!, betType: v.betType!,
      hideNoVol: v.hideNoVol, hideSettled: v.hideSettled, period: v.period!, tab: v.tab!, propStat: v.propStat };
  },
};
export const spreadTotalReading: WorkingViewDefinition<Pick<GameLinesReading, 'priceMode' | 'marketSide'>> = {
  id: 'game-spread-total-reading-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<GameLinesReading>;
    return ['maker', 'taker', 'market'].includes(v.priceMode ?? '') && ['yes', 'no'].includes(v.marketSide ?? '')
      ? { priceMode: v.priceMode!, marketSide: v.marketSide! } : null;
  },
};
export const moneylineReading = { ...spreadTotalReading, id: 'game-moneyline-reading-v1' };

/** #1078 — the full-game Spread/Total line a trader picked, by exact identity
 * (`spreadLineKey`/`totalLineKey`) plus the label shown when it is missing.
 * Visible selection only: never an aim, an expansion or a ticket. */
export interface PickedLine { key: string; label: string }
export interface SpreadTotalLines { spread: PickedLine | null; total: PickedLine | null }
function parsePickedLine(input: unknown): PickedLine | null | undefined {
  if (input === null) return null;
  if (!input || typeof input !== 'object') return undefined;
  const v = input as Partial<PickedLine>;
  return typeof v.key === 'string' && v.key.length > 0 && v.key.length <= 256
    && typeof v.label === 'string' && v.label.length <= 64
    ? { key: v.key, label: v.label } : undefined;
}
export const spreadTotalLineReading: WorkingViewDefinition<SpreadTotalLines> = {
  id: 'game-spread-total-line-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<SpreadTotalLines>;
    const spread = parsePickedLine(v.spread);
    const total = parsePickedLine(v.total);
    return spread === undefined || total === undefined ? null : { spread, total };
  },
};
